import type { MenuType } from '@/modules/Customer/interface/Menu';
import { usePathname } from 'next/navigation';
import { useEffect, type FC } from 'react';
import { useSession } from 'next-auth/react';
import {
  ChevronLeft,
  Close,
  type IconsNames,
} from '@eqi-investimentos/icons/web';
import {
  Flex,
  Popover,
  PopoverContent,
  Text,
  useDisclosure,
} from '@eqi-investimentos/react';
import { useT } from '@/libs/i18n/utils/useT';
import { useSendClickEvent } from '@/modules/Customer/http/client/mutations/useSendClickEvent';
import { IconButton } from '@/components/elements/IconButton';
import { handleMenuItemClick } from './utils';
import { MenuItem } from './Item';

export const Submenu: FC<SeeMoreButtonProps> = ({
  title,
  buttonName,
  icon,
  items,
  shouldClose,
  isOverflow,
}) => {
  const t = useT();
  const pathname = usePathname();
  const { data: session } = useSession();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const { mutate: sendClickEvent } = useSendClickEvent();

  useEffect(() => {
    if (shouldClose) onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldClose]);

  return (
    <Popover {...{ isOpen, onOpen, onClose }}>
      <MenuItem
        iconName={icon}
        name={buttonName}
        onClick={onOpen}
        isOverflow={isOverflow}
      />
      <PopoverContent
        pt="l"
        pb="0"
        px="m"
        borderRadius="2xs"
        boxShadow="0 2px 2px 0 rgba(0, 0, 0, 0.25)"
        bg="background.primary"
        w="17.5rem"
        maxH="calc(100vh - 6rem - 9.75rem)"
        overflowY="auto"
        overflowX="hidden"
        rootProps={{
          transform: isOverflow ? 'translateY(0) !important' : undefined,
        }}
      >
        <Flex justifyContent="space-between" alignItems="center" mb="m">
          {title ? (
            <Flex gap="2xs" alignItems="center">
              <IconButton
                icon={ChevronLeft}
                onButtonClick={onClose}
                ariaLabel="Voltar ao menu anterior"
                iconProps={{ color: 'content.primary' }}
              />
              <Text size="xl" fontWeight="medium">
                {title}
              </Text>
            </Flex>
          ) : (
            <Text size="xl" fontWeight="medium" color="content.primary">
              {t('attributes.whatYouNeed')}
            </Text>
          )}
          <IconButton
            icon={Close}
            onButtonClick={onClose}
            ariaLabel="Fechar menu"
            iconProps={{ color: 'content.primary' }}
          />
        </Flex>
        {items.map(({ iconName, name, target, url, submenu }) =>
          submenu ? (
            <Submenu
              key={`menu-item-${name}`}
              buttonName={name}
              title={name}
              icon={iconName}
              items={submenu}
              shouldClose={shouldClose}
              isOverflow
            />
          ) : (
            <MenuItem
              key={`menu-item-${name}`}
              iconName={iconName}
              isActive={pathname === url}
              isOverflow
              name={name}
              target={target}
              url={url}
              onClick={() =>
                handleMenuItemClick(
                  url,
                  !!session?.access_token,
                  sendClickEvent
                )
              }
            />
          )
        )}
      </PopoverContent>
    </Popover>
  );
};

type SeeMoreButtonProps = {
  title?: string;
  buttonName: string;
  icon: IconsNames | null;
  items: MenuType[];
  shouldClose: boolean;
  isOverflow?: boolean;
};
